/**
 * PATH: packages/ui/src/contextMenu/contextMenuGeometry.ts
 * FILE_ID: AIHUB-SHARED-CTXMENU-GEOM-001
 * PURPOSE: Pure I→O helpers that decide WHERE a context menu (and its
 *          nested submenu panels) should sit, given a click point, the
 *          panel's measured size, and the viewport size.
 *
 * WHY: The geometry rules ("if menu doesn't fit below, flip up", "if a
 *      submenu doesn't fit to the right, flip to the left") were
 *      previously inline inside `ContextMenu.tsx`, which meant every
 *      time someone tweaked them they had to rely on visual QA. Pulling
 *      them out:
 *
 *        1. Lets us unit-test the rules with plain numbers (no jsdom,
 *           no React, no `getBoundingClientRect` mocking).
 *        2. Makes the rules SHARED between the root context menu and
 *           the nested submenu — same logic, two call sites.
 *        3. Bug-fix history can read like a maths spec rather than
 *           "what does this if-chain do again?".
 *
 * FLOW (root menu):
 *   ┌──────────────┐    ┌──────────────────────┐    ┌──────────────┐
 *   │ click x,y +  │ →  │ computeMenuPosition  │ →  │ {left, top,  │
 *   │ measured WxH │    │ (prefers downward,   │    │  flipped: …} │
 *   │ + viewport   │    │  flips when needed)  │    │              │
 *   └──────────────┘    └──────────────────────┘    └──────────────┘
 *
 * FLOW (submenu):
 *   ┌─────────────────┐    ┌─────────────────────────┐    ┌──────────────┐
 *   │ trigger button  │ →  │ computeSubmenuPlacement │ →  │ {side, top,  │
 *   │ rect + panel    │    │ (prefers right, flips   │    │  offsetCss}  │
 *   │ WxH + viewport  │    │  to left, slides up)    │    │              │
 *   └─────────────────┘    └─────────────────────────┘    └──────────────┘
 *
 * DEPENDENCIES: none (pure functions; types only)
 *
 * RELATED:
 *   - ./PositionedContextMenu.tsx (consumer)
 *   - ./contextMenuGeometry.test.ts
 */

// ──────────────────────────────────────────────────────────
// Root context menu — placed at a click point
// ──────────────────────────────────────────────────────────

export interface MenuPositionInput {
  /** Mouse coordinates of the originating right-click, in viewport
   *  pixels (e.g. React MouseEvent.clientX / clientY). */
  clickX: number
  clickY: number
  /** Measured panel size AFTER first paint. Callers pass
   *  el.getBoundingClientRect().width / height. */
  menuWidth: number
  menuHeight: number
  /** Viewport size — window.innerWidth / window.innerHeight. */
  viewportWidth: number
  viewportHeight: number
  /** Min distance to keep from viewport edges. Default 8px. */
  margin?: number
}

export interface MenuPositionOutput {
  /** Final CSS left value (px). */
  left: number
  /** Final CSS top value (px). */
  top: number
  /** Diagnostic flag — did we flip from the default down/right
   *  anchor? Surfaces in tests + future telemetry. */
  flipped: 'none' | 'vertical' | 'horizontal' | 'both'
}

/**
 * Pure helper — decide where a context menu should sit given a click
 * point and a measured panel size.
 *
 * Rules (mirrors macOS native context menus):
 *
 *   1. Default: anchor the menu's top-left at the click point. The
 *      cursor lands on the first row.
 *   2. If menuHeight > spaceBelow AND spaceAbove >= menuHeight, FLIP
 *      vertically: anchor the menu's bottom-left at the click point
 *      (top = clickY - menuHeight). The cursor lands on the LAST row.
 *      Keeps the right-clicked row visible above the cursor.
 *   3. If menuWidth > spaceRight AND spaceLeft >= menuWidth, FLIP
 *      horizontally: anchor the menu's top-right at the click point
 *      (left = clickX - menuWidth).
 *   4. After flips, CLAMP to keep at least `margin` distance from
 *      every viewport edge.
 *
 * `flipped` tells callers which axes were inverted — useful for tests
 * and for picking the right enter-animation in a follow-up.
 */
export function computeMenuPosition(input: MenuPositionInput): MenuPositionOutput {
  const margin = input.margin ?? 8
  const { clickX, clickY, menuWidth, menuHeight, viewportWidth, viewportHeight } = input

  const spaceBelow = viewportHeight - clickY
  const spaceAbove = clickY
  const spaceRight = viewportWidth - clickX
  const spaceLeft = clickX

  // Vertical anchor — prefer downward; flip up only when down won't
  // fit AND up DOES. This is the rule the user explicitly asked for:
  // "should have popped up a bit above". Keeps the right-clicked row
  // visible above the cursor instead of buried under the menu.
  let top: number
  let verticalFlipped = false
  if (menuHeight > spaceBelow && spaceAbove >= menuHeight) {
    top = clickY - menuHeight
    verticalFlipped = true
  } else {
    top = clickY
  }

  // Horizontal anchor — prefer rightward; flip left if cramped.
  let left: number
  let horizontalFlipped = false
  if (menuWidth > spaceRight && spaceLeft >= menuWidth) {
    left = clickX - menuWidth
    horizontalFlipped = true
  } else {
    left = clickX
  }

  // Final clamp — never let any edge come within `margin` of the
  // viewport. Belt + braces for the rare case where the menu is
  // genuinely taller than the entire viewport.
  top = Math.max(margin, Math.min(top, viewportHeight - menuHeight - margin))
  left = Math.max(margin, Math.min(left, viewportWidth - menuWidth - margin))

  const flipped: MenuPositionOutput['flipped'] =
    verticalFlipped && horizontalFlipped
      ? 'both'
      : verticalFlipped
        ? 'vertical'
        : horizontalFlipped
          ? 'horizontal'
          : 'none'

  return { left, top, flipped }
}

// ──────────────────────────────────────────────────────────
// Nested submenu — placed relative to a parent trigger button
// ──────────────────────────────────────────────────────────

export interface SubmenuPlacementInput {
  /** Viewport-space rect of the parent trigger button. Pass
   *  trigger.getBoundingClientRect(). Only the four edges are read,
   *  so callers don't need a real DOMRect (POJOs work in tests). */
  triggerRect: {
    top: number
    bottom: number
    left: number
    right: number
  }
  /** Measured panel size AFTER first paint. */
  panelWidth: number
  panelHeight: number
  viewportWidth: number
  viewportHeight: number
  /** Min distance to keep from viewport edges. Default 8px. */
  margin?: number
  /** Gap between the trigger button and the panel. Default 4px
   *  (matches the existing `ml-1` Tailwind class). */
  gap?: number
}

export interface SubmenuPlacement {
  /** Which side of the trigger the panel ended up on. */
  side: 'right' | 'left'
  /** CSS values to apply to the panel. EITHER `left` is set (when
   *  side === 'right') OR `right` is set (when side === 'left') —
   *  callers must zero-out the unused one. Both expressed as
   *  viewport-space pixels. */
  left: number | null
  right: number | null
  /** CSS top (viewport-space pixels). */
  top: number
  /** Diagnostic — did we slide vertically to fit? */
  verticalAdjusted: boolean
  /** Dynamic max-height for the panel. The consumer should apply
   *  this as an inline style so the panel can't bleed past the
   *  available vertical room in the chosen anchor direction. Always
   *  positive; falls back to `viewportHeight - 2*margin` in
   *  worst-case viewports. */
  maxHeight: number
}

/**
 * Pure helper — decide where a nested submenu panel should sit.
 *
 * Rules:
 *
 *   1. Default: panel sits to the RIGHT of the trigger, top-aligned
 *      with the trigger, separated by `gap`.
 *   2. If panel.width > spaceRightOfTrigger AND spaceLeftOfTrigger
 *      >= panel.width: FLIP to the LEFT (panel's right edge anchored
 *      to trigger's left edge).
 *   3. Vertical (BUG-2026-05-13-CTXMENU-SUBMENU-COVERS-TRIGGER):
 *      - If panel fits below the trigger anchor → default top-align.
 *      - Else if there is MORE ROOM ABOVE the trigger than below →
 *        ANCHOR-FLIP-UP: pin panel.bottom at triggerRect.bottom so
 *        the panel extends UPWARD from the trigger. Trigger stays
 *        visible at the bottom edge of the panel (user requested:
 *        "I should see benko board upwards" — the long list of
 *        boards rises out of the trigger row rather than burying it
 *        under a slid-up panel).
 *      - Else stay below and slide up (legacy behaviour) so the
 *        panel still fits the viewport.
 *   4. `maxHeight` is returned so the consumer can constrain the
 *      panel's effective height to the available room in the chosen
 *      direction. Without this, the slide-up branch above could place
 *      a panel whose top sits *behind* the trigger row, hiding it.
 *      The CSS `max-h-[80vh]` class is a static cap; this output is a
 *      dynamic cap that adapts to where the trigger actually is.
 *   5. Final clamp: keep `margin` distance from all four viewport
 *      edges.
 *
 * Note: panel positions are RETURNED in viewport-space pixels. The
 * consumer is expected to render the panel as `position: fixed`
 * (NOT `position: absolute` relative to the trigger's container),
 * since absolute positioning inside a scrollable / clipping ancestor
 * would re-introduce the very overflow bug we're solving.
 */
export function computeSubmenuPlacement(
  input: SubmenuPlacementInput,
): SubmenuPlacement {
  const margin = input.margin ?? 8
  const gap = input.gap ?? 4
  const { triggerRect, panelWidth, panelHeight, viewportWidth, viewportHeight } = input

  const spaceRight = viewportWidth - triggerRect.right
  const spaceLeft = triggerRect.left

  // Decide side. Prefer right; flip to left only when right won't fit
  // AND left does. If neither fits comfortably, default to right (the
  // final clamp will pull it back into the viewport).
  const wantRight = panelWidth + gap + margin <= spaceRight
  const canLeft = panelWidth + gap + margin <= spaceLeft
  const side: 'right' | 'left' = wantRight ? 'right' : canLeft ? 'left' : 'right'

  let left: number | null = null
  let right: number | null = null
  if (side === 'right') {
    left = triggerRect.right + gap
  } else {
    // Use a viewport-space "right" — distance from viewport's right
    // edge. Avoids needing the panel width to compute left.
    right = viewportWidth - (triggerRect.left - gap)
  }

  // Vertical: three-branch placement.
  //
  // spaceBelow = room from trigger.top to viewport.bottom (where the
  // panel would extend if we keep the default top-align).
  // spaceAbove = room from viewport.top to trigger.bottom (where the
  // panel could extend if we flip the anchor).
  //
  // We compute against the FULL un-capped panelHeight so the chooser
  // can favour the direction that actually fits the content. The
  // maxHeight output below then truncates if neither direction has
  // room for the full content.
  const spaceBelow = viewportHeight - triggerRect.top - margin
  const spaceAbove = triggerRect.bottom - margin

  let top: number
  let verticalAdjusted = false
  let maxHeight: number

  if (panelHeight <= spaceBelow) {
    // Fits cleanly below — default top-align.
    top = triggerRect.top
    maxHeight = spaceBelow
  } else if (spaceAbove > spaceBelow) {
    // Anchor-flip-up: pin panel.bottom to trigger.bottom. Panel
    // extends upward; trigger stays visible at the panel's bottom
    // edge. maxHeight constrains the panel to the room actually
    // available above so the panel never overlaps the trigger row.
    top = Math.max(margin, triggerRect.bottom - Math.min(panelHeight, spaceAbove))
    verticalAdjusted = true
    maxHeight = spaceAbove
  } else {
    // More room below than above (or roughly equal) — keep the panel
    // below the trigger and slide up to fit the viewport. Trigger
    // may end up partially behind the panel; we cap maxHeight at the
    // post-slide window so the cap matches what the user can scroll.
    top = Math.max(margin, viewportHeight - panelHeight - margin)
    verticalAdjusted = true
    maxHeight = viewportHeight - 2 * margin
  }

  if (top < margin) {
    top = margin
    verticalAdjusted = true
  }

  // Horizontal clamp (only when on the right; left-side panels are
  // already anchored to the right edge, which is naturally clamped).
  if (side === 'right' && left != null) {
    if (left + panelWidth + margin > viewportWidth) {
      left = Math.max(margin, viewportWidth - panelWidth - margin)
    }
  }

  return { side, left, right, top, verticalAdjusted, maxHeight }
}
